/**
 * Présentation des sous-postes issus du plan comptable Zoho : libellés,
 * regroupement par poste, tri (générique en tête, puis comptes par code) et
 * arborescence par compte parent. Fonctions pures, sans accès au DOM.
 */

import type { OrigineSousPoste } from '@scc/core';
import { rangPoste } from '../../postes.js';

/** Ce que ces fonctions lisent d'un sous-poste. */
export interface SousPosteAffichable {
  readonly id: string;
  readonly poste: string;
  readonly libelle: string;
  readonly code: string;
  readonly origine: OrigineSousPoste;
  readonly parentId: string | null;
}

export const ORIGINES: Readonly<Record<OrigineSousPoste, string>> = {
  zoho: 'Compte Zoho',
  generique: 'Générique',
  manuelle: 'Manuel',
  initiale: 'Initial',
};

/**
 * « code — libellé », le code étant omis quand il est vide ou déjà en tête du
 * libellé (nombre de comptes Zoho s'intitulent « C6414 - Indemnités diverses »).
 */
export function libelleCompte(s: { readonly code: string; readonly libelle: string }): string {
  if (s.code === '' || s.libelle.trim().toLowerCase().startsWith(s.code.toLowerCase())) return s.libelle;
  return `${s.code} — ${s.libelle}`;
}

/** Rang de l'origine dans un poste : le générique en tête, les comptes Zoho ensuite. */
function rangOrigine(o: OrigineSousPoste): number {
  return o === 'generique' ? 0 : o === 'zoho' ? 2 : 1;
}

/** Générique d'abord, puis les sous-postes propres à l'application, puis les comptes par code. */
export function comparerSousPostes(a: SousPosteAffichable, b: SousPosteAffichable): number {
  return (
    rangOrigine(a.origine) - rangOrigine(b.origine) ||
    (a.code === '' ? 1 : 0) - (b.code === '' ? 1 : 0) ||
    a.code.localeCompare(b.code, 'fr', { numeric: true }) ||
    a.libelle.localeCompare(b.libelle, 'fr')
  );
}

/** Sous-postes regroupés par poste, les postes dans l'ordre de la nomenclature. */
export function grouperSousPostesParPoste<T extends SousPosteAffichable>(liste: readonly T[]): [string, T[]][] {
  const groupes = new Map<string, T[]>();
  for (const s of liste) groupes.set(s.poste, [...(groupes.get(s.poste) ?? []), s]);
  return [...groupes.entries()].sort(([a], [b]) => rangPoste(a) - rangPoste(b) || a.localeCompare(b));
}

export interface LigneArbre<T> {
  readonly sousPoste: T;
  /** Nombre d'ancêtres présents dans la liste fournie. */
  readonly profondeur: number;
}

/**
 * Ordonne des sous-postes en arborescence : chaque compte suit son parent, en
 * retrait. Un compte dont le parent n'est pas dans la liste (autre poste, ou
 * absent du filtre) devient une racine. Tolère un cycle de parentés.
 */
export function ordonnerArbre<T extends SousPosteAffichable>(liste: readonly T[]): LigneArbre<T>[] {
  const ids = new Set(liste.map((s) => s.id));
  const enfants = new Map<string, T[]>();
  const racines: T[] = [];
  for (const s of liste) {
    if (s.parentId !== null && s.parentId !== s.id && ids.has(s.parentId)) {
      enfants.set(s.parentId, [...(enfants.get(s.parentId) ?? []), s]);
    } else {
      racines.push(s);
    }
  }
  const sortie: LigneArbre<T>[] = [];
  const vus = new Set<string>();
  const descendre = (s: T, profondeur: number): void => {
    if (vus.has(s.id)) return;
    vus.add(s.id);
    sortie.push({ sousPoste: s, profondeur });
    for (const e of [...(enfants.get(s.id) ?? [])].sort(comparerSousPostes)) descendre(e, profondeur + 1);
  };
  for (const r of [...racines].sort(comparerSousPostes)) descendre(r, 0);
  // Les membres d'un cycle n'ont pas de racine : on les rattache à la fin.
  for (const s of [...liste].sort(comparerSousPostes)) descendre(s, 0);
  return sortie;
}
